import { useEffect, useRef, useState, type FocusEvent, type KeyboardEvent, type ReactNode } from 'react';
import { Input, TD } from '@/components/ui';
import { ApiError } from '@/lib/api';
import {
  applyNameEdit,
  CONTACT_NAME_LABELS,
  splitFullName,
  type ContactNameField,
  type ContactNameParts,
} from '@/lib/contact-name';
import { cx } from '@/lib/cx';
import type { Contact, ContactInput } from '@/lib/types';
import { validateContactDraft } from './rules';

/**
 * The three name fields of one contact in the contacts manager, edited as ONE
 * group (owner, 2026-10-16).
 *
 * - Clicking any of the three opens all three, so `applyNameEdit` can carry a
 *   keystroke in one field into the other two (both ways, only while they
 *   agree — see `lib/contact-name.ts`).
 * - Enter, or focus leaving the GROUP (not moving between the three), commits
 *   ONE PATCH carrying only the changed name keys, so the three are never
 *   half-saved. Esc reverts all three.
 * - An untouched group never PATCHes (an empty PATCH is a 422).
 *
 * Two renderings over the same hook: `ContactNameCells` (three `<TD>`, for the
 * desktop table row) and `ContactNameFields` (stacked, for the card).
 *
 * The parts shown for a legacy row with neither part stored are
 * `splitFullName`'s guess, and a save sends what was shown (the same rule as
 * `ContactDetailPage.nameEditPatch`).
 */

export interface ContactNameCellsProps {
  contact: Contact;
  canEdit: boolean;
  /** Why the names cannot be edited. Rendered only when `canEdit` is false. */
  disabledReason?: string;
  /** Performs the PATCH. Throws (an `ApiError` ideally) on failure. */
  onSave: (patch: ContactInput) => Promise<void>;
}

/** Server limits, mirrored from `ContactFormModal` / `ContactDetailPage`. */
const MAX: Record<ContactNameField, number> = { name: 240, firstName: 120, lastName: 120 };

/** Display order: Full name, First name, Last name. */
const ORDER: readonly ContactNameField[] = ['name', 'firstName', 'lastName'];

const SAVED_MS = 4000;
const EM_DASH = '—';

type Phase = 'idle' | 'editing' | 'saving';
type FieldErrors = Partial<Record<ContactNameField, string>>;

/** What the screen shows: stored parts, or the split guess when neither is stored. */
function shownParts(contact: Contact): ContactNameParts {
  const firstName = contact.firstName?.trim() ?? '';
  const lastName = contact.lastName?.trim() ?? '';
  const name = contact.name ?? '';
  if (firstName || lastName) return { firstName, lastName, name };
  return { ...splitFullName(name), name };
}

function trimmed(parts: ContactNameParts): ContactNameParts {
  return { firstName: parts.firstName.trim(), lastName: parts.lastName.trim(), name: parts.name.trim() };
}

/** Changed keys only, compared with what is STORED. Null when nothing differs. */
function namePatch(contact: Contact, draft: ContactNameParts): ContactInput | null {
  const next = trimmed(draft);
  const patch: ContactInput = {};
  if (next.firstName !== (contact.firstName?.trim() ?? '')) patch.firstName = next.firstName;
  if (next.lastName !== (contact.lastName?.trim() ?? '')) {
    patch.lastName = next.lastName === '' ? null : next.lastName;
  }
  if (next.name !== (contact.name ?? '')) patch.name = next.name;
  return Object.keys(patch).length > 0 ? patch : null;
}

function validate(draft: ContactNameParts): FieldErrors {
  const next = trimmed(draft);
  const shared = validateContactDraft({ name: next.name, firstName: next.firstName, lastName: next.lastName });
  const errors: FieldErrors = {};
  for (const field of ORDER) {
    if (next[field].length > MAX[field]) {
      errors[field] = `${CONTACT_NAME_LABELS[field]} must be ${MAX[field]} characters or fewer.`;
    } else if (shared[field]) {
      errors[field] = shared[field];
    }
  }
  return errors;
}

function routeSaveError(caught: unknown): { fields: FieldErrors; form: string | null } {
  if (caught instanceof ApiError) {
    const routed = caught.fieldErrors;
    const fields: FieldErrors = {};
    let unrouted: string | null = null;
    for (const [path, message] of Object.entries(routed)) {
      if (path === 'name' || path === 'firstName' || path === 'lastName') fields[path] = message;
      else unrouted = unrouted ?? message;
    }
    if (Object.keys(fields).length > 0) return { fields, form: unrouted ?? caught.formMessage };
    return { fields, form: unrouted ?? caught.formMessage ?? caught.message };
  }
  return { fields: {}, form: 'The change could not be saved.' };
}

function useNameGroup({ contact, canEdit, onSave }: ContactNameCellsProps) {
  const [phase, setPhase] = useState<Phase>('idle');
  const [draft, setDraft] = useState<ContactNameParts>(() => shownParts(contact));
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const inputs = useRef<Partial<Record<ContactNameField, HTMLInputElement | null>>>({});
  const triggers = useRef<Partial<Record<ContactNameField, HTMLButtonElement | null>>>({});
  const focusInput = useRef<ContactNameField | null>(null);
  const focusTrigger = useRef<ContactNameField | null>(null);
  const activeField = useRef<ContactNameField>('name');
  const seq = useRef(0);

  useEffect(() => {
    if (phase === 'editing' && focusInput.current) {
      inputs.current[focusInput.current]?.focus();
      focusInput.current = null;
    }
    if (phase === 'idle' && focusTrigger.current) {
      triggers.current[focusTrigger.current]?.focus();
      focusTrigger.current = null;
    }
  }, [phase]);

  useEffect(() => {
    if (!saved) return;
    const timer = setTimeout(() => setSaved(false), SAVED_MS);
    return () => clearTimeout(timer);
  }, [saved]);

  const shown = shownParts(contact);

  function begin(field: ContactNameField) {
    if (!canEdit || phase !== 'idle') return;
    setDraft(shownParts(contact));
    setFieldErrors({});
    setFormError(null);
    setSaved(false);
    activeField.current = field;
    focusInput.current = field;
    setPhase('editing');
  }

  function revert(returnFocus: boolean) {
    setDraft(shownParts(contact));
    setFieldErrors({});
    setFormError(null);
    focusTrigger.current = returnFocus ? activeField.current : null;
    setPhase('idle');
  }

  async function commit(returnFocus: boolean) {
    if (phase !== 'editing') return;
    const patch = namePatch(contact, draft);
    const before = trimmed(shownParts(contact));
    const after = trimmed(draft);
    const untouched =
      before.firstName === after.firstName && before.lastName === after.lastName && before.name === after.name;
    if (patch === null || untouched) {
      revert(returnFocus);
      return;
    }
    const errors = validate(draft);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setFormError(null);
      return;
    }
    const ticket = seq.current + 1;
    seq.current = ticket;
    setFieldErrors({});
    setFormError(null);
    setPhase('saving');
    try {
      await onSave(patch);
    } catch (caught) {
      if (seq.current !== ticket) return;
      const routed = routeSaveError(caught);
      setFieldErrors(routed.fields);
      setFormError(routed.form);
      setPhase('editing');
      return;
    }
    if (seq.current !== ticket) return;
    setSaved(true);
    focusTrigger.current = returnFocus ? activeField.current : null;
    setPhase('idle');
  }

  function change(field: ContactNameField, value: string) {
    activeField.current = field;
    setDraft((current) => applyNameEdit(current, field, value));
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (phase !== 'editing') return;
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      revert(true);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      void commit(true);
    }
  }

  function onBlur(event: FocusEvent<HTMLInputElement>) {
    const next = event.relatedTarget;
    if (next instanceof HTMLElement && ORDER.some((field) => inputs.current[field] === next)) return;
    void commit(false);
  }

  function renderField(field: ContactNameField): ReactNode {
    const label = CONTACT_NAME_LABELS[field];
    const error = fieldErrors[field];
    if (phase === 'idle') {
      const value = shown[field].trim();
      const display = value === '' ? <span className="text-fg-subtle">{EM_DASH}</span> : value;
      if (!canEdit) return <span className="min-w-0 [overflow-wrap:anywhere]">{display}</span>;
      return (
        <button
          type="button"
          ref={(element) => {
            triggers.current[field] = element;
          }}
          onClick={() => begin(field)}
          className={cx(
            'group -mx-1.5 -my-1 flex w-full min-w-0 items-start gap-1.5 rounded-md px-1.5 py-1 text-left',
            'transition-colors duration-150 ease-out motion-reduce:transition-none',
            'hover:bg-surface-hover',
          )}
        >
          <span className="sr-only">Edit {label}: </span>
          <span className="min-w-0 flex-1 [overflow-wrap:anywhere]">{display}</span>
          <PencilGlyph />
        </button>
      );
    }
    const errorId = `${contact.id}-${field}-error`;
    return (
      <div className="grid min-w-0 gap-1">
        <Input
          ref={(element) => {
            inputs.current[field] = element;
          }}
          aria-label={label}
          value={draft[field]}
          maxLength={MAX[field]}
          autoComplete="off"
          readOnly={phase === 'saving'}
          invalid={Boolean(error)}
          aria-describedby={error ? errorId : undefined}
          onChange={(event) => change(field, event.target.value)}
          onKeyDown={onKeyDown}
          onBlur={onBlur}
        />
        {error && (
          <p id={errorId} role="alert" className="m-0 text-xs text-danger [overflow-wrap:anywhere]">
            {error}
          </p>
        )}
      </div>
    );
  }

  /** The group's one status line: saving, saved, a form-level refusal, or why it is read-only. */
  function renderStatus(disabledReason: string | undefined): ReactNode {
    if (phase === 'saving') return <p role="status" className="m-0 text-xs text-fg-subtle">Saving…</p>;
    if (formError) {
      return (
        <p role="alert" className="m-0 text-xs text-danger [overflow-wrap:anywhere]">
          {formError}
        </p>
      );
    }
    if (saved && phase === 'idle') return <p role="status" className="m-0 text-xs text-success">Saved.</p>;
    if (!canEdit && disabledReason) {
      return <p className="m-0 text-xs text-fg-subtle [overflow-wrap:anywhere]">{disabledReason}</p>;
    }
    return null;
  }

  return { renderField, renderStatus };
}

/** Desktop: three `<TD>`s (Full name, First name, Last name) for a `<TR>`. */
export function ContactNameCells(props: ContactNameCellsProps) {
  const { renderField, renderStatus } = useNameGroup(props);
  const status = renderStatus(props.disabledReason);
  return (
    <>
      <TD verticalAlign="top" className="min-w-40">
        <div className="grid min-w-0 gap-1">
          {renderField('name')}
          {status}
        </div>
      </TD>
      <TD verticalAlign="top" className="min-w-32">
        {renderField('firstName')}
      </TD>
      <TD verticalAlign="top" className="min-w-32">
        {renderField('lastName')}
      </TD>
    </>
  );
}

/** Card (below md): the same three fields stacked, each with its label. */
export function ContactNameFields(props: ContactNameCellsProps) {
  const { renderField, renderStatus } = useNameGroup(props);
  return (
    <div className="grid min-w-0 gap-2">
      {ORDER.map((field) => (
        <div key={field} className="grid min-w-0 gap-0.5">
          <span className="text-xs text-fg-muted">{CONTACT_NAME_LABELS[field]}</span>
          <div className="min-w-0 text-sm text-fg">{renderField(field)}</div>
        </div>
      ))}
      {renderStatus(props.disabledReason)}
    </div>
  );
}

function PencilGlyph() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={cx(
        'mt-0.5 h-3.5 w-3.5 shrink-0 text-fg-subtle opacity-60',
        'transition-opacity duration-150 ease-out motion-reduce:transition-none',
        'group-hover:opacity-100 group-focus-visible:opacity-100',
      )}
    >
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  );
}
